/**
 * Utilitaire pour analyser une liste de numéros de téléphone
 * et compter les parents par type
 */

const API_BASE_URL =
  process.env.REACT_APP_BASE_API_URL ||
  "https://recette-backend.genimi.io/api/v1/";

/**
 * Analyse une liste de numéros de téléphone et retourne les statistiques
 * @param {string[]} phoneNumbers - Liste des numéros de téléphone
 * @returns {Promise<Object>} - Statistiques par type
 */
export async function analyzePhoneNumbers(phoneNumbers) {
  try {
    // Récupérer tous les parents avec une limite élevée pour éviter la pagination
    const parentsResponse = await fetch(
      `${API_BASE_URL}parents/?limit=100000&offset=0`,
    );
    const parentsData = await parentsResponse.json();
    const parents = parentsData.data || parentsData.results || parentsData;

    // Récupérer tous les élèves pour obtenir les classes avec une limite élevée
    const elevesResponse = await fetch(
      `${API_BASE_URL}eleves/?limit=10000&offset=0`,
    );
    const elevesData = await elevesResponse.json();
    const eleves = elevesData.data || elevesData.results || elevesData;

    // Normaliser les numéros (enlever espaces, tirets, etc.)
    const normalizePhone = (phone) => {
      if (!phone) return "";
      return phone.toString().replace(/[\s\-()]/g, "");
    };

    const normalizedPhones = phoneNumbers.map(normalizePhone);

    // Filtrer les parents qui correspondent aux numéros
    const matchingParents = parents.filter((parent) => {
      const parentPhone = normalizePhone(parent.phone);
      return normalizedPhones.includes(parentPhone);
    });

    // Compter par type
    const stats = {
      total: matchingParents.length,
      byType: {},
      byProvenance: {},
      details: matchingParents.map((parent) => {
        // Trouver les enfants de ce parent
        const enfants = eleves.filter(
          (eleve) =>
            eleve.parentId === parent.id || eleve.parent?.id === parent.id,
        );
        const classes = enfants
          .map((e) => e.classe?.name || e.classe?.sub_title || "N/A")
          .join(", ");

        // Déterminer le type affiché selon les règles métier
        let typeAffiche;
        const parentType = (parent.type || "").toUpperCase();

        if (parentType === "CONFIRMED") {
          typeAffiche = "CLIENT";
        } else if (parentType === "PROSPECT") {
          typeAffiche = "PROSPECT";
        } else if (parentType === "CONTACT") {
          // Vérifier si le parent a utilisé du crédit (< 100)
          const credits = parent.credits || 0;
          const initialCredits = 100; // Crédit initial supposé
          const hasUsedCredits = credits < initialCredits && credits >= 0;

          if (hasUsedCredits) {
            typeAffiche = "Utilisateur";
          } else if (parent.countEnfant > 0 || enfants.length > 0) {
            // CONTACT avec enfant => PROSPECT
            typeAffiche = "PROSPECT";
          } else {
            // CONTACT sans enfant => CONTACT
            typeAffiche = "CONTACT";
          }
        } else {
          typeAffiche = parent.type || "UNKNOWN";
        }

        return {
          phone: parent.phone,
          fullName: parent.fullName,
          type: parent.type, // Type original
          typeAffiche: typeAffiche, // Type à afficher
          provenance: parent.provenance,
          credits: parent.credits,
          countEnfant: parent.countEnfant,
          classesEnfants: classes || "Aucune classe",
        };
      }),
    };

    // Compter par type affiché
    matchingParents.forEach((parent) => {
      const enfants = eleves.filter(
        (eleve) =>
          eleve.parentId === parent.id || eleve.parent?.id === parent.id,
      );
      let typeAffiche;
      const parentType = (parent.type || "").toUpperCase();

      if (parentType === "CONFIRMED") {
        typeAffiche = "CLIENT";
      } else if (parentType === "PROSPECT") {
        typeAffiche = "PROSPECT";
      } else if (parentType === "CONTACT") {
        // Vérifier si le parent a utilisé du crédit (< 100)
        const credits = parent.credits || 0;
        const initialCredits = 100;
        const hasUsedCredits = credits < initialCredits && credits >= 0;

        if (hasUsedCredits) {
          typeAffiche = "Utilisateur";
        } else if (parent.countEnfant > 0 || enfants.length > 0) {
          typeAffiche = "PROSPECT";
        } else {
          typeAffiche = "CONTACT";
        }
      } else {
        typeAffiche = parent.type || "UNKNOWN";
      }
      stats.byType[typeAffiche] = (stats.byType[typeAffiche] || 0) + 1;
    });

    // Compter par provenance
    matchingParents.forEach((parent) => {
      const provenance = parent.provenance || "UNKNOWN";
      stats.byProvenance[provenance] =
        (stats.byProvenance[provenance] || 0) + 1;
    });

    // Numéros non trouvés
    const foundPhones = matchingParents.map((p) => normalizePhone(p.phone));
    const notFound = normalizedPhones.filter(
      (phone) => !foundPhones.includes(phone),
    );

    return {
      ...stats,
      notFound: notFound.length,
      notFoundNumbers: notFound,
    };
  } catch (error) {
    console.error("Erreur lors de l'analyse:", error);
    throw error;
  }
}

/**
 * Affiche les résultats dans la console de manière formatée
 */
export function displayStats(stats) {
  console.log("\n=== STATISTIQUES DES NUMÉROS ===\n");
  console.log(`Total de numéros analysés: ${stats.total}`);

  console.log("\n--- Par Type ---");
  Object.entries(stats.byType).forEach(([type, count]) => {
    console.log(`${type}: ${count}`);
  });

  console.log("\n--- Par Provenance ---");
  Object.entries(stats.byProvenance).forEach(([provenance, count]) => {
    console.log(`${provenance}: ${count}`);
  });

  if (stats.notFound > 0) {
    console.log(`\n⚠️ Numéros non trouvés: ${stats.notFound}`);
    console.log("Numéros:", stats.notFoundNumbers.join(", "));
  }

  console.log("\n--- Détails ---");
  console.table(stats.details);
}

// Export pour utilisation dans la console du navigateur
if (typeof window !== "undefined") {
  window.analyzePhoneNumbers = analyzePhoneNumbers;
  window.displayStats = displayStats;
}
